import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { OpportunityFilters } from '@/components/opportunities/OpportunityFilters';
import { OpportunityList } from '@/components/opportunities/OpportunityList';
import { supabase } from '@/integrations/supabase/client';
import { FilterOptions, Opportunity } from '@/types';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Opportunities() {
  const [searchParams] = useSearchParams();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState<Opportunity[]>([]);
  const [filters, setFilters] = useState<FilterOptions>({});
  const [loading, setLoading] = useState(true);

  // Fetch approved opportunities from database
  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        const { data, error } = await supabase
          .from('opportunities')
          .select('*')
          .eq('status', 'approved')
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Transform database data to match Opportunity type
        const transformedData: Opportunity[] = (data || []).map((opp: any) => ({
          id: opp.id,
          title: opp.title,
          company: opp.company,
          type: opp.type,
          location: opp.location,
          arrondissement: opp.arrondissement,
          description: opp.description,
          responsibilities: opp.responsibilities,
          requirements: opp.requirements,
          salary: opp.salary,
          deadline: opp.deadline ? new Date(opp.deadline) : new Date(),
          publishedAt: new Date(opp.created_at),
          applicationUrl: opp.application_url,
          phoneNumber: opp.contact_phone,
        }));

        setOpportunities(transformedData);
        setFilteredOpportunities(transformedData);
      } catch (error: any) {
        console.error('Error fetching opportunities:', error);
        toast.error('Erreur lors du chargement des opportunités');
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunities();
  }, []);

  // Apply filters from URL params
  useEffect(() => {
    const type = searchParams.get('type');
    const search = searchParams.get('search');
    
    if (type) {
      setFilters(prev => ({ ...prev, type: [type as any] }));
    }
    if (search) {
      setFilters(prev => ({ ...prev, searchQuery: search }));
    }
  }, [searchParams]);

  // Filter opportunities based on current filters
  useEffect(() => {
    let filtered = [...opportunities];
    
    if (filters.type && filters.type.length > 0) {
      filtered = filtered.filter(opp => filters.type!.includes(opp.type));
    }
    
    if (filters.contractType && filters.contractType.length > 0) {
      filtered = filtered.filter(opp => 
        opp.contractType && filters.contractType!.includes(opp.contractType)
      );
    }
    
    if (filters.location && filters.location.length > 0) {
      filtered = filtered.filter(opp => 
        filters.location!.includes(opp.arrondissement || opp.location)
      );
    }
    
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(opp => 
        opp.title.toLowerCase().includes(query) ||
        opp.company.toLowerCase().includes(query) ||
        opp.description.toLowerCase().includes(query)
      );
    }
    
    setFilteredOpportunities(filtered);
  }, [filters, opportunities]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-poppins font-bold text-foreground mb-4">
            Toutes les Opportunités
          </h1>
          <p className="text-lg text-muted-foreground font-inter">
            {filteredOpportunities.length} opportunité{filteredOpportunities.length > 1 ? 's' : ''} disponible{filteredOpportunities.length > 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1">
            <OpportunityFilters filters={filters} onFiltersChange={setFilters} />
          </aside>
          
          <div className="lg:col-span-3">
            <OpportunityList opportunities={filteredOpportunities} />
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
